import VerticalAlignBottomIcon from "@mui/icons-material/VerticalAlignBottom";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import { Fab } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

export const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  const waitForElem = () =>
    new Promise((resolve) => {
      const observer = new MutationObserver(() => {
        const elem = document.getElementById(hash.slice(1));

        if (elem) {
          observer.disconnect();
          resolve(elem);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    });

  useEffect(() => {
    window.scroll(0, 0);

    if (hash) {
      const elem = document.getElementById(hash.slice(1));

      if (elem) {
        elem.scrollIntoView({ behavior: "smooth" });
      } else {
        waitForElem().then((res) => {
          res.scrollIntoView({ behavior: "smooth" });
        });
      }
    }
  }, [pathname, hash]);

  return null;
};

export const JumpScrollButton = () => {
  const SCROLL_TOP = "top";
  const SCROLL_BOTTOM = "bottom";

  const [direction, setDirection] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  const activeScrollEl = useRef(null);
  const timeoutRef = useRef(null);

  const handleScroll = useCallback((e) => {
    if (!e?.target) return;

    window.requestAnimationFrame(() => {
      const scrollingEl =
        e.target === document ? document.documentElement : e.target;
      activeScrollEl.current = scrollingEl;

      const windowHeight = window.innerHeight;
      const scrollHeight = scrollingEl.scrollHeight;
      const scrollPosition = scrollingEl.scrollTop;

      setDirection(
        scrollPosition < scrollHeight / 2 ? SCROLL_BOTTOM : SCROLL_TOP,
      ); // If the user hasn't scrolled past one viewport height set the direction to scroll down

      setIsVisible(scrollPosition > windowHeight); // Check if page is scrollable

      setIsScrolling((prev) => (!prev ? true : prev)); // Set isScrolling to true (only if false) when capturing scroll event

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 3000); // Delay resetting isScrolling for 3 secs
    });
  }, []);

  const scrollToPosition = () => {
    if (!activeScrollEl.current) return;

    activeScrollEl.current.scrollTo({
      top:
        direction === SCROLL_BOTTOM ? activeScrollEl.current.scrollHeight : 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    handleScroll();

    document.addEventListener("scroll", handleScroll, true);
    document.addEventListener("resize", handleScroll, true);

    return () => {
      document.removeEventListener("scroll", handleScroll, true);
      document.removeEventListener("resize", handleScroll, true);
    };
  }, [handleScroll]);

  // Render nothing if the page is not scrollable
  if (!isVisible) return null;

  return (
    <Fab
      color="subtleGrey"
      title={`Jump to ${direction}`}
      aria-label={`Jump to ${direction}`}
      onClick={scrollToPosition}
      sx={{
        position: "fixed",
        bottom: 20,
        right: 20,
        transition: "opacity 0.5s ease-in-out",
        opacity: isScrolling && isVisible ? 0.9 : 0,
      }}
      size="small"
    >
      {direction === SCROLL_BOTTOM ? (
        <VerticalAlignBottomIcon />
      ) : (
        <VerticalAlignTopIcon />
      )}
    </Fab>
  );
};
